// Bridge between the configurator promo popup (mounted inside the route tree)
// and FloatingMessenger (mounted once in Layout, owns the webchat instance).
// The popup can't reach the useWebchat hook directly, so it publishes a
// request here; FloatingMessenger subscribes and drives the chat.
//
// The lead itself no longer travels this way — the popup POSTs it and only then
// publishes here, so this bus is chat decoration: an auto-reply carrying the
// project link. Hence no parking for a late subscriber; `request` says whether
// the chat took it, and the popup confirms the lead itself when it did not.

export type PromoChatRequest = {
  /** Shareable configurator link that must reach the manager. */
  projectUrl: string
  /** Phone entered in the popup, already accepted by the backend. */
  phone: string
}

const PHONE_LEFT_KEY = 'webchat_phone_left'
const CONSENT_LOG_KEY = 'promo_popup_consent'

type Listener = (req: PromoChatRequest) => void

let listener: Listener | null = null

export const promoChatBus = {
  /** Mirrored by FloatingMessenger so the popup can respect an open panel. */
  state: {
    panelOpen: false,
    /** Chat teaser and promo popup are mutually exclusive on screen. */
    teaserVisible: false,
    popupVisible: false,
  },

  /** @returns true when a connected chat took the request. */
  request(req: PromoChatRequest): boolean {
    if (!listener) return false
    listener(req)
    return true
  },

  subscribe(fn: Listener): () => void {
    listener = fn
    return () => {
      if (listener === fn) listener = null
    }
  },
}

/** True when the visitor already left a phone in any chat flow (persistent). */
export function hasLeftPhone(): boolean {
  try {
    return !!localStorage.getItem(PHONE_LEFT_KEY)
  } catch {
    return false
  }
}

export function markPhoneLeft(): void {
  try {
    localStorage.setItem(PHONE_LEFT_KEY, '1')
  } catch {
    /* ignore */
  }
}

/** GDPR trace for the promo flow: where and when consent was given. */
export function recordPromoConsent(): void {
  try {
    localStorage.setItem(
      CONSENT_LOG_KEY,
      JSON.stringify({ source: 'promo_popup', at: new Date().toISOString() })
    )
  } catch {
    /* ignore */
  }
}
